import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import CostBreakdownChart from "../../components/events/CostBreakdownChart";
import EventKpiStrip, { deriveEventKpis } from "../../components/events/EventKpiStrip";
import ProjectedActualDonut from "../../components/events/ProjectedActualDonut";
import { KpiStripSkeleton, QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import { useEventDashboard } from "../../hooks/useEventDashboard";
import { workspaceNav } from "../../routes/workspaceNavigation";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./EventOverviewPage.css";

type Props = {
  workspace: WorkspaceId;
};

/**
 * Event Overview tab.
 *
 * Sprint 14 deliberately keeps this sparse — "pretty basic Overview, we do
 * not want to clutter this space". Only the KPI strip, the projected-vs-actual
 * donut and the cost pie live here; the setup checklist and the rail
 * (Show Health / Budget Summary / Sell Through) are mounted by
 * EventWorkspaceLayout. Everything analytical moved to the P&L tab.
 */
const EventOverviewPage: React.FC<Props> = ({ workspace }) => {
  const { eventId } = useParams<{ eventId: string }>();
  const { query, data, errorMessage } = useEventDashboard(workspace, eventId);
  const eventsPath = workspaceNav.events(workspace);

  if (!eventId) {
    return <Navigate to={eventsPath} replace />;
  }

  if (query.isPending) {
    return (
      <div className="app-shell-dash dashboard-page">
        <KpiStripSkeleton count={6} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="app-shell-dash dashboard-page">
        <QueryErrorBanner
          title="Dashboard request failed"
          message={errorMessage || "Unknown error"}
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
        <Link to={eventsPath} className="app-shell-back-link">
          ← Back to events
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-shell-dash dashboard-page">
        <div className="eventos-empty">No dashboard payload for this id.</div>
        <Link to={eventsPath} className="app-shell-back-link">
          ← Back to events
        </Link>
      </div>
    );
  }

  const { projectedRevenue, projectedProfit, actualRevenue, actualProfit } = deriveEventKpis(data);
  const costRows = data.chart_data.cost_breakdown ?? [];

  return (
    <div className="app-shell-dash dashboard-page">
      <EventKpiStrip data={data} />

      <div className="event-overview__charts">
        <div className="eventos-card">
          <div className="eventos-label">Projected vs actual</div>
          <div className="eventos-subtle event-overview__chart-sub">Revenue and profit</div>
          <ProjectedActualDonut
            projectedRevenue={projectedRevenue}
            actualRevenue={actualRevenue}
            projectedProfit={projectedProfit}
            actualProfit={actualProfit}
          />
        </div>

        <div className="eventos-card">
          <div className="eventos-label">Event cost</div>
          <div className="eventos-subtle event-overview__chart-sub">Expense distribution</div>
          <CostBreakdownChart rows={costRows} variant="pie" />
        </div>
      </div>
    </div>
  );
};

export default EventOverviewPage;
